import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import ConvertLawyerModal from './ConvertLawyerModal';
import useOrganizationService from '../../../app/services/hooks/useOrganizationService';
import PageHeader from '../../../components/PageHeader/PageHeader';
import DefaultCard from '../../../components/default/DefaultCard';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';
import ButtonTableAction from '../../../components/buttons/ButtonTableAction';
import InputField from '../../../components/InputField';
import SelectTwo from '../../../components/SelectTwo';
import { OrganizationData, PlanLimits, PlanUsage, formatCLP, formatDate, limitText, statusLabel } from '../types';

interface OrganizationRow extends OrganizationData {
    plan_name: string | null;
    plan_is_fallback: boolean;
    subscription_status: string | null;
    is_manual_assignment: boolean;
    subscription_end_date: string | null;
    usage: PlanUsage;
    limits: PlanLimits;
}

interface Metrics {
    organizations_total: number;
    firms: number;
    independents: number;
    suspended: number;
    new_last_30_days: number;
    paying_subscriptions: number;
    morose_subscriptions: number;
    manual_plans: number;
    mrr_clp: number;
    members_total: number;
    clients_total: number;
    brand_applications_total: number;
    video_revenue_clp: number;
}

const TYPE_OPTIONS = [
    { value: '', label: 'Todos los tipos' },
    { value: 'firm', label: 'Estudios' },
    { value: 'independent', label: 'Independientes' }
];
const STATUS_OPTIONS = [
    { value: '', label: 'Todos los estados' },
    { value: 'active', label: 'Activas' },
    { value: 'suspended', label: 'Suspendidas' }
];

const MetricTile: React.FC<{ label: string; value: string | number; icon: string; hint?: string }> = ({ label, value, icon, hint }) => (
    <div className="col-6 col-lg-3 mb-3">
        <div className="card h-100 mb-0">
            <div className="card-body py-3">
                <div className="d-flex align-items-center gap-2 text-muted small mb-1"><i className={`fas ${icon}`} />{label}</div>
                <div className="fs-4 fw-bold">{value}</div>
                {hint && <small className="text-muted">{hint}</small>}
            </div>
        </div>
    </div>
);

/**
 * Panel SaaS de Monere: estudios y abogados independientes, sus planes y uso.
 * Muestra cantidades, nunca el contenido de los casos de cada estudio.
 */
const OrganizationList: React.FC = () => {
    const history = useHistory();
    const { getAdminOrganizations, getAdminMetrics, fetchingAdminOrganizations } = useOrganizationService();

    const [rows, setRows] = useState<OrganizationRow[]>([]);
    const [metrics, setMetrics] = useState<Metrics | null>(null);
    const [filters, setFilters] = useState({ search: '', organization_type: '', status: '' });
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [showConvert, setShowConvert] = useState(false);
    const perPage = 20;

    const load = (nextPage = page) => {
        getAdminOrganizations({ ...filters, page: nextPage, per_page: perPage }, {
            onSuccess: (response) => {
                setRows(response.data.organizations);
                setTotal(response.data.total);
            },
            onError: (response) => toast.error(response.message)
        });
    };

    useEffect(() => {
        getAdminMetrics({ onSuccess: (response) => setMetrics(response.data), onError: (r) => toast.error(r.message) });
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
            load(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [filters]);

    const pages = Math.max(1, Math.ceil(total / perPage));

    return (
        <div className="container-fluid">
            <PageHeader
                breadcrumbs={[{ label: 'Inicio', path: '/intranet' }, { label: 'Estudios y Abogados', isCurrent: true }]}
                title="Estudios y Abogados"
                description="Organizaciones que usan Monere como SaaS: planes, suscripciones y uso. Los casos de cada estudio son privados del estudio."
            />
            <div className="d-flex justify-content-end gap-2 mb-3">
                <ButtonGeneral onClick={() => history.push('/intranet/scheduler')} title="Programador de tareas" icon="fa-clock"
                               size="small" variant="outline" />
                <ButtonGeneral onClick={() => setShowConvert(true)} title="Convertir abogado existente" icon="fa-user-tie" size="small" />
            </div>
            <ConvertLawyerModal show={showConvert} onClose={() => setShowConvert(false)}
                                onConverted={(organizationId) => { setShowConvert(false); history.push(`/intranet/organizations/${organizationId}`); }} />

            {metrics && (
                <div className="row" data-testid="saas-metrics">
                    <MetricTile label="Organizaciones" value={metrics.organizations_total} icon="fa-building"
                                hint={`${metrics.firms} estudios · ${metrics.independents} independientes`} />
                    <MetricTile label="Ingreso mensual recurrente" value={formatCLP(metrics.mrr_clp)} icon="fa-chart-line"
                                hint={`Neto sin IVA · ${metrics.paying_subscriptions} suscripciones pagadas`} />
                    <MetricTile label="Morosas" value={metrics.morose_subscriptions} icon="fa-exclamation-triangle"
                                hint={`${metrics.suspended} suspendidas · ${metrics.manual_plans} planes manuales`} />
                    <MetricTile label="Uso" value={metrics.members_total} icon="fa-users"
                                hint={`usuarios · ${metrics.clients_total} clientes · ${metrics.brand_applications_total} marcas`} />
                </div>
            )}

            <DefaultCard>
                <div className="row align-items-end mb-3">
                    <div className="col-md-4 mb-2">
                        <InputField label="Buscar" value={filters.search} icon="fa-search" placeholder="Nombre, RUT o correo"
                                    onChange={(value) => setFilters({ ...filters, search: value })} />
                    </div>
                    <div className="col-md-3 mb-2">
                        <SelectTwo label="Tipo" inputValue={filters.organization_type} options={TYPE_OPTIONS}
                                   onChange={(option: any) => setFilters({ ...filters, organization_type: option?.value || '' })} />
                    </div>
                    <div className="col-md-3 mb-2">
                        <SelectTwo label="Estado" inputValue={filters.status} options={STATUS_OPTIONS}
                                   onChange={(option: any) => setFilters({ ...filters, status: option?.value || '' })} />
                    </div>
                </div>

                <div className="table-responsive">
                    <table className="table table-hover align-middle" data-testid="organizations-table">
                        <thead>
                            <tr>
                                <th>Organización</th>
                                <th>Plan</th>
                                <th className="text-center">Usuarios</th>
                                <th className="text-center">Clientes</th>
                                <th className="text-center">Marcas</th>
                                <th>Estado</th>
                                <th>Alta</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {fetchingAdminOrganizations && rows.length === 0 ? (
                                <tr><td colSpan={8} className="text-center py-4"><div className="spinner-border text-primary" /></td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={8} className="text-center text-muted py-4">No hay organizaciones con esos filtros.</td></tr>
                            ) : rows.map((row) => (
                                <tr key={row.id}>
                                    <td>
                                        <span className="fw-bold d-block">{row.name}</span>
                                        <small className="text-muted">{row.organization_type_display} · {row.owner_email}</small>
                                    </td>
                                    <td>
                                        <span className="d-block">{row.plan_name || '—'}</span>
                                        <small className="text-muted">
                                            {row.plan_is_fallback ? 'sin suscripción vigente' : row.is_manual_assignment ? 'asignado por Monere' : statusLabel(row.subscription_status) || 'gratuito'}
                                        </small>
                                    </td>
                                    <td className="text-center">{row.usage.seats}/{limitText(row.limits.max_seats)}</td>
                                    <td className="text-center">{row.usage.clients}/{limitText(row.limits.max_clients)}</td>
                                    <td className="text-center">{row.usage.brand_applications}</td>
                                    <td>
                                        <span className={`badge ${row.status === 'active' ? 'bg-success' : 'bg-danger'}`}>{row.status_display}</span>
                                    </td>
                                    <td><small>{formatDate(row.created_at)}</small></td>
                                    <td className="text-center">
                                        <ButtonTableAction callbackFunction={() => history.push(`/intranet/organizations/${row.id}`)}
                                                           title="Ver" classIcon="fa-eye" colorIcon="text-info" sizeIcon="16" />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {pages > 1 && (
                    <div className="d-flex justify-content-end align-items-center gap-2">
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page <= 1}
                                onClick={() => { setPage(page - 1); load(page - 1); }}>Anterior</button>
                        <small>Página {page} de {pages}</small>
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= pages}
                                onClick={() => { setPage(page + 1); load(page + 1); }}>Siguiente</button>
                    </div>
                )}
            </DefaultCard>
        </div>
    );
};

export default OrganizationList;
